import { Badge, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import type { BizyScorecardMetric, BizyScorecardNode } from '../../types/nodes';
import { useBizyChatActions } from '../context/actions-context';
import { BIZY_KIT_I18N_NAMESPACE } from '../i18n';
import { useEvidenceDrill } from '../lib/use-evidence-drill';
import type { BizyNodeProps } from './node-props';

const SENTIMENT_COLOR: Record<BizyScorecardMetric['sentiment'], string> = {
	positive: 'green',
	neutral: 'gray',
	negative: 'red',
};

/**
 * Renders the Data Agent's Scorecard node: 1–4 KPI cards that lead a rich
 * answer as the at-a-glance verdict. When drilling is enabled the whole card
 * is a tap target that sends a "tell me more" message.
 */
export function ScorecardNode({ node }: BizyNodeProps) {
	const { payload } = node as BizyScorecardNode;
	const { title, metrics } = payload;
	const { t, i18n } = useTranslation(BIZY_KIT_I18N_NAMESPACE);
	const { followUpsDisabled } = useBizyChatActions();
	const { drillEnabled, drill } = useEvidenceDrill();
	const badgeBorderShade = useColorModeValue('200', '400');

	const percentileBadge = (pct: number) => {
		const n = Math.round(pct);
		const category = new Intl.PluralRules(i18n.language, { type: 'ordinal' }).select(n);
		const suffix = t(`scorecard.ordinalSuffix.${category}`, { defaultValue: t('scorecard.ordinalSuffix.other') });
		const word = t('scorecard.percentileWord', { defaultValue: 'pct' });
		return `${n}${suffix} ${word}`;
	};

	const cards = metrics.filter((metric) => metric.label.trim().length > 0).slice(0, 4);
	if (cards.length === 0) return null;

	const onSelectMetric = drillEnabled ? (metric: BizyScorecardMetric) => drill('scorecard', metric.label, { title }) : undefined;

	return (
		<Flex direction="column" gap={2} mb={4} data-testid="bizy-scorecard">
			{title ? (
				<Text fontSize="md" fontWeight="semibold" color="text.default">
					{title}
				</Text>
			) : null}
			<Flex gap={3} flexWrap="wrap">
				{cards.map((metric, idx) => {
					const color = SENTIMENT_COLOR[metric.sentiment];
					const tappable = !!onSelectMetric && !followUpsDisabled;
					return (
						<Flex
							key={idx}
							onClick={tappable ? () => onSelectMetric?.(metric) : undefined}
							onKeyDown={
								tappable
									? (e) => {
											if (e.key === 'Enter' || e.key === ' ') {
												e.preventDefault();
												onSelectMetric?.(metric);
											}
										}
									: undefined
							}
							role={tappable ? 'button' : undefined}
							tabIndex={tappable ? 0 : undefined}
							aria-label={tappable ? t('drillInto', { label: metric.label, defaultValue: 'Drill into {{label}}' }) : undefined}
							direction="column"
							gap={1}
							flex="1 1 140px"
							minW="140px"
							textAlign="left"
							borderWidth="1px"
							borderColor="border.soft"
							borderRadius={12}
							px={3}
							py={2.5}
							opacity={followUpsDisabled ? 0.5 : 1}
							cursor={tappable ? 'pointer' : 'default'}
							transition="background 0.15s ease, border-color 0.15s ease"
							{...(tappable && { _hover: { bg: 'secondary.bgAlpha', borderColor: 'border.default' } })}
						>
							<Text fontSize="xs" color="text.muted" lineHeight="short" noOfLines={1}>
								{metric.label}
							</Text>
							<Flex align="baseline" gap={2}>
								<Text fontSize="2xl" fontWeight="bold" color="text.default" lineHeight="short">
									{metric.value}
								</Text>
								{metric.percentile !== undefined ? (
									<Badge colorScheme={color} variant="subtle" fontSize="xs" borderWidth="1px" borderColor={`${color}.${badgeBorderShade}`}>
										{percentileBadge(metric.percentile)}
									</Badge>
								) : null}
							</Flex>
							{metric.peerMedian !== undefined ? (
								<Text fontSize="xs" color="text.muted" lineHeight="short">
									{t('scorecard.peerMedian', { value: metric.peerMedian, defaultValue: 'vs {{value}} peer median' })}
								</Text>
							) : null}
						</Flex>
					);
				})}
			</Flex>
		</Flex>
	);
}
